<script lang="ts">
    import Chart from 'chart.js/auto'
    import ChartDataLabels from 'chartjs-plugin-datalabels'
    import { onMount, onDestroy } from 'svelte/internal'
    import dayjs from 'dayjs'
    import { EventSourcesRepository } from '../../modules/eventsApi/event-sources/event-sources.repository'
    import { EventSourcesService } from '../../modules/eventsApi/event-sources/event-sources.service'
    import type { CommunicationStatsDto } from '../../modules/eventsApi/event-sources/dto/communication-stats.dto'
    import DateSelect from '../_common/date-select.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import Zact from '../_common/z/zact.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import { ChannelType } from '../../modules/brokerApi/channels/channels.enum'
    import type { Channel } from '../../modules/brokerApi/channels/channel'

    const esR = new EventSourcesRepository()
    const esS = new EventSourcesService(esR)

    // par já usado no dashboard-home; separação validada para daltonismo em tema claro e escuro
    const COLOR_RECEIVED = '#6179F3'
    const COLOR_SENT = '#379E75'
    const MUTED = '#666'

    const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']

    // tipos de canal que geram eventos de whatsapp — os demais não fazem sentido neste filtro
    const WHATSAPP_TYPES = [
        ChannelType.wa_user,
        ChannelType.wa_evo2_user,
        ChannelType.wa_evo2_zandu,
        ChannelType.wa_uazapi_user,
        ChannelType.wa_uazapi_zandu,
        ChannelType.waba_user,
    ]

    let dates: { from: Date; to: Date } = window.api.utils.dates.currentMonth()
    let channelId: string = ''
    let channelOptions: { value: string; label: string }[] = [{ value: '', label: 'Todos os canais' }]
    let show = false
    let stats: CommunicationStatsDto = null
    let charts: Chart[] = []

    let containerConversations: HTMLCanvasElement
    let containerMessages: HTMLCanvasElement
    let containerHeatmap: HTMLCanvasElement

    onMount(async () => {
        await loadChannels()
        await load()
    })

    async function loadChannels() {
        try {
            // updateStatus=false: aqui só queremos os nomes, não vale consultar cada provedor
            const channels: Channel[] = await window.api.brokers.channels.getAll({ updateStatus: false })
            channelOptions = [
                { value: '', label: 'Todos os canais' },
                ...channels
                    .filter((el) => WHATSAPP_TYPES.includes(el.type))
                    .map((el) => ({ value: el.channelId, label: el.label || el.externalName || el.channelId })),
            ]
        } catch (error) {
            // sem a lista o filtro fica só com 'Todos' — não é motivo para derrubar o dashboard
            console.log('nao foi possivel carregar os canais', error)
        }
    }

    function channelChanged() {
        load()
    }

    onDestroy(() => {
        charts.forEach((c) => c.destroy())
    })

    function dateChanged(event) {
        dates = { from: event.detail.from, to: event.detail.to }
        load()
    }

    async function load() {
        try {
            show = true
            stats = await esS.getCommunicationStats({
                from: dayjs(dates.from).toISOString(),
                to: dayjs(dates.to).toISOString(),
                sources: ['whatsapp'],
                // o banco guarda UTC; sem o deslocamento, mensagens da noite caem no dia seguinte
                tzOffsetMinutes: -new Date().getTimezoneOffset(),
                channelId: channelId || undefined,
            })

            charts.forEach((c) => c.destroy())
            charts = []

            generateConversations()
            generateMessages()
            generateHeatmap()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    /** preenche os dias sem movimento: sem isso a linha "pula" períodos parados */
    function buildDays() {
        let result: { day: string; label: string; conversations: number; received: number; sent: number }[] = []
        let cursor = dayjs(dates.from).startOf('day')
        const last = dayjs(dates.to).startOf('day')
        while (cursor.isBefore(last) || cursor.isSame(last)) {
            const key = cursor.format('YYYY-MM-DD')
            const found = stats?.daily?.find((el) => el.day === key)
            result.push({
                day: key,
                label: cursor.format('DD/MM'),
                conversations: found?.conversations || 0,
                received: found?.received || 0,
                sent: found?.sent || 0,
            })
            cursor = cursor.add(1, 'day')
        }
        return result
    }

    function generateConversations() {
        const days = buildDays()
        const chart = new Chart(containerConversations, {
            type: 'line',
            data: {
                labels: days.map((d) => d.label),
                datasets: [
                    {
                        label: 'Pessoas',
                        data: days.map((d) => d.conversations),
                        borderColor: COLOR_RECEIVED,
                        backgroundColor: hexToRgba(COLOR_RECEIVED, 0.25),
                        borderWidth: 2,
                        pointRadius: days.length > 45 ? 0 : 4,
                        tension: 0.3,
                        fill: true,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: { display: false },
                    datalabels: { display: false },
                    tooltip: { callbacks: { label: (ctx) => ctx.parsed.y + ' pessoa(s)' } },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { color: MUTED, maxRotation: 0, autoSkip: true } },
                    y: { beginAtZero: true, grid: { display: false }, ticks: { color: MUTED, precision: 0 } },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    function generateMessages() {
        const days = buildDays()
        const chart = new Chart(containerMessages, {
            type: 'bar',
            data: {
                labels: days.map((d) => d.label),
                datasets: [
                    {
                        label: 'Recebidas',
                        data: days.map((d) => d.received),
                        backgroundColor: COLOR_RECEIVED,
                        borderRadius: 4,
                    },
                    {
                        label: 'Enviadas',
                        data: days.map((d) => d.sent),
                        backgroundColor: COLOR_SENT,
                        borderRadius: 4,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: { position: 'top', labels: { color: MUTED } },
                    datalabels: { display: false },
                },
                scales: {
                    x: {
                        stacked: true,
                        grid: { display: false },
                        ticks: { color: MUTED, maxRotation: 0, autoSkip: true },
                    },
                    y: {
                        stacked: true,
                        beginAtZero: true,
                        grid: { display: false },
                        ticks: { color: MUTED, precision: 0 },
                    },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    /**
     * Mapa de calor dia da semana x hora. O chart.js não tem heatmap nativo: usamos um scatter
     * com marcas quadradas e opacidade proporcional ao volume — uma única matiz, do claro ao escuro.
     */
    function generateHeatmap() {
        const max = Math.max(1, ...(stats?.hourly || []).map((el) => el.messages))
        let points = []
        for (const item of stats?.hourly || []) {
            points.push({ x: item.hour, y: item.weekday, v: item.messages, p: item.conversations })
        }

        const chart = new Chart(containerHeatmap, {
            type: 'scatter',
            data: {
                datasets: [
                    {
                        label: 'Mensagens',
                        data: points,
                        pointStyle: 'rect',
                        radius: 11,
                        hoverRadius: 13,
                        backgroundColor: (ctx: any) => {
                            const value = ctx.raw?.v || 0
                            return hexToRgba(COLOR_RECEIVED, 0.15 + 0.85 * (value / max))
                        },
                        borderWidth: 0,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    datalabels: { display: false },
                    tooltip: {
                        callbacks: {
                            title: (items: any) => WEEKDAYS[items[0].raw.y] + ' · ' + items[0].raw.x + 'h',
                            label: (ctx: any) => ctx.raw.v + ' mensagem(ns) de ' + ctx.raw.p + ' pessoa(s)',
                        },
                    },
                },
                scales: {
                    x: {
                        min: -0.5,
                        max: 23.5,
                        grid: { display: false },
                        ticks: { color: MUTED, stepSize: 2, callback: (v) => v + 'h' },
                    },
                    y: {
                        min: -0.5,
                        max: 6.5,
                        grid: { display: false },
                        ticks: { color: MUTED, stepSize: 1, callback: (v) => WEEKDAYS[v] || '' },
                    },
                },
            },
            plugins: [ChartDataLabels],
        })
        charts.push(chart)
    }

    function hexToRgba(hex: string, alpha: number) {
        const n = parseInt(hex.replace('#', ''), 16)
        return 'rgba(' + ((n >> 16) & 255) + ', ' + ((n >> 8) & 255) + ', ' + (n & 255) + ', ' + alpha + ')'
    }
</script>

<div class="base-dash">
    <Zact>
        <DateSelect startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        <div class="channel-filter">
            <Zinput type="select" label="Canal" options={channelOptions} bind:value={channelId} on:change={channelChanged} />
        </div>
    </Zact>

    <ZContainer class="h-space">
        <Zframe size={4} sizeM={6} title="Pessoas" headline="Pessoas diferentes que enviaram mensagem no período.">
            <div class="kpi">{stats?.totals?.people ?? '-'}</div>
        </Zframe>
        <Zframe size={4} sizeM={6} title="Mensagens recebidas" headline="Total de mensagens que chegaram no período.">
            <div class="kpi">{stats?.totals?.received ?? '-'}</div>
        </Zframe>
        <Zframe size={4} sizeM={6} title="Mensagens por pessoa" headline="Média de mensagens recebidas por pessoa que entrou em contato.">
            <div class="kpi">{stats?.totals?.messagesPerPerson ?? '-'}</div>
        </Zframe>
        <!-- "Pessoas novas" oculto temporariamente: dependia de getReturningPeople, desativada no
             backend por custo de performance (ver event-sources.service.ts). Reativar os dois juntos
             quando o endpoint for otimizado. -->
        <!--
        <Zframe size={3} sizeM={6} title="Pessoas novas" headline="Pessoas que falaram com você pela primeira vez neste período.">
            <div class="kpi">
                {stats?.totals?.newPeople ?? '-'}
                {#if stats?.totals?.people > 0}
                    <span class="kpi-sub">de {stats.totals.people}</span>
                {/if}
            </div>
        </Zframe>
        -->

        <Zframe
            size={12}
            title="Pessoas por dia"
            headline="Quantas pessoas diferentes enviaram mensagem em cada dia. Quem manda várias mensagens no mesmo dia conta uma vez."
        >
            <div class="chart-box">
                <canvas bind:this={containerConversations}></canvas>
            </div>
        </Zframe>

        <Zframe
            size={12}
            title="Mensagens por dia"
            headline="Volume de mensagens recebidas e enviadas. As enviadas dependem do provedor do canal: o WhatsApp Business API não registra envios, e na uazapi entram apenas as respostas feitas pelo celular."
        >
            <div class="chart-box">
                <canvas bind:this={containerMessages}></canvas>
            </div>
        </Zframe>

        <Zframe
            size={12}
            title="Quando te procuram"
            headline="Volume de mensagens recebidas por dia da semana e hora. Quanto mais forte a marca, maior o movimento — útil para dimensionar o atendimento."
        >
            <div class="chart-box chart-box_tall">
                <canvas bind:this={containerHeatmap}></canvas>
            </div>
        </Zframe>
    </ZContainer>
</div>
<LoadSpinner delay={300} {show} />

<style>
    .channel-filter {
        min-width: 240px;
        margin-left: 8px;
    }
    .chart-box {
        position: relative;
        width: 100%;
        height: 320px;
    }
    .chart-box_tall {
        height: 380px;
    }
    .kpi {
        font-size: 32px;
        font-weight: 600;
        text-align: center;
        padding: 12px 0;
    }
    .kpi-sub {
        font-size: 14px;
        font-weight: 400;
        opacity: 0.6;
        margin-left: 6px;
    }
</style>
